{
  "blocks": [
    {
      "id": "dx-adenomyosis",
      "kind": "condition",
      "tags": ["her", "evidence-based"],
      "title": "Adenomyosis",
      "subtitle": "Endometrial tissue grows into the uterine muscle wall",
      "body": {
        "conditionName": "Adenomyosis",
        "category": "structural",
        "description": "Adenomyosis occurs when tissue that normally lines the uterus (endometrium) grows into the muscular wall of the uterus (myometrium). This causes the uterus to enlarge and can lead to heavy, painful periods. It is increasingly recognized as a significant factor in implantation failure and recurrent pregnancy loss.",
        "symptoms": [
          "Heavy menstrual bleeding (menorrhagia)",
          "Severe menstrual cramps that worsen with age",
          "Chronic pelvic pain or pressure",
          "Painful intercourse (dyspareunia)",
          "Enlarged, tender uterus on exam",
          "Bloating during periods",
          "Difficulty conceiving or recurrent implantation failure"
        ],
        "howDiagnosed": "Suspected on transvaginal ultrasound (heterogeneous myometrium, asymmetric uterine wall thickening, myometrial cysts). MRI is the most accurate non-invasive test, showing junctional zone thickening > 12 mm. Definitive diagnosis historically required hysterectomy pathology, but imaging is now considered reliable for clinical purposes.",
        "impactOnFertility": "Adenomyosis impairs fertility through multiple mechanisms: disrupted uterine contractility affecting sperm transport, impaired implantation due to altered endometrial-myometrial junction, increased inflammation, and abnormal uterine blood flow. Studies suggest it reduces IVF success rates by 25-40%, particularly implantation and live birth rates.",
        "prevalence": "Affects an estimated 20-35% of women, though many cases are asymptomatic. More common in women over 30 and those with prior uterine surgery.",
        "treatmentOptions": [
          "GnRH agonist suppression (2-3 months) before embryo transfer",
          "Extended oral contraceptive or progestin pre-treatment",
          "IVF with frozen embryo transfer (allowing uterine rest)",
          "Adenomyomectomy in severe focal cases",
          "Anti-inflammatory medications for symptom management",
          "Gestational carrier in severe refractory cases"
        ],
        "prognosis": "With appropriate pre-treatment (particularly GnRH agonist suppression before FET), pregnancy rates improve significantly. Mild adenomyosis has a better prognosis than diffuse severe disease. Early recognition and targeted treatment are key to improving outcomes.",
        "forWhom": "her"
      },
      "sources": [
        {
          "title": "Adenomyosis and infertility: a literature review",
          "authors": "Vannuccini S, Tosti C, Carmona F, et al.",
          "journal": "Reproductive BioMedicine Online",
          "year": 2017,
          "url": "https://pubmed.ncbi.nlm.nih.gov/28734548/",
          "evidenceLevel": "review"
        },
        {
          "title": "Good practice recommendations on recurrent implantation failure",
          "authors": "ESHRE Working Group on RIF (Cimadomo D et al.)",
          "journal": "Human Reproduction Open",
          "year": 2023,
          "url": "https://pubmed.ncbi.nlm.nih.gov/37325540/",
          "evidenceLevel": "guideline"
        }
      ]
    },
    {
      "id": "dx-recurrent-pregnancy-loss",
      "kind": "condition",
      "tags": ["her", "both-partners", "evidence-based"],
      "title": "Recurrent pregnancy loss",
      "subtitle": "Two or more consecutive pregnancy losses before 20 weeks",
      "body": {
        "conditionName": "Recurrent pregnancy loss (RPL)",
        "category": "reproductive",
        "description": "Recurrent pregnancy loss is defined as two or more consecutive clinical pregnancy losses before 20 weeks of gestation. While a single miscarriage is common (affecting 15-25% of pregnancies), recurrent loss affects a smaller group and often warrants investigation into underlying causes, though in about half of cases, no specific cause is identified.",
        "symptoms": [
          "Two or more consecutive miscarriages",
          "Bleeding and cramping in early pregnancy",
          "Passage of tissue",
          "Positive pregnancy test followed by loss"
        ],
        "howDiagnosed": "Evaluation includes: karyotype of both partners (chromosomal analysis), uterine cavity assessment (HSG, saline sonogram, or hysteroscopy), thrombophilia panel (antiphospholipid antibodies, Factor V Leiden, prothrombin mutation), thyroid function, prolactin, and hemoglobin A1c. Products of conception from losses may be sent for chromosomal analysis.",
        "impactOnFertility": "RPL does not necessarily mean a couple cannot have a successful pregnancy. Even without treatment, couples with unexplained RPL have a 60-75% chance of successful pregnancy in the next attempt. IVF with PGT-A can help when chromosomal issues are suspected by selecting euploid embryos.",
        "prevalence": "Affects approximately 1-2% of couples trying to conceive. Risk increases with maternal age and number of prior losses.",
        "treatmentOptions": [
          "IVF with PGT-A (preimplantation genetic testing for aneuploidy)",
          "PGT-SR if a balanced translocation is found in either partner",
          "Low-dose aspirin and/or heparin for antiphospholipid syndrome",
          "Progesterone supplementation in early pregnancy",
          "Thyroid optimization if subclinical hypothyroidism is found",
          "Surgical correction of uterine anomalies if present",
          "Lifestyle optimization and emotional support"
        ],
        "prognosis": "Prognosis is generally hopeful. Even with 3 prior losses and no identified cause, the chance of a successful next pregnancy is approximately 60-75%. When a treatable cause is found (e.g., antiphospholipid syndrome, thyroid dysfunction), targeted treatment significantly improves outcomes.",
        "forWhom": "both"
      },
      "sources": [
        {
          "title": "ESHRE guideline: recurrent pregnancy loss: an update in 2023",
          "authors": "Bender Atik R, Christiansen OB, Elson J, et al.",
          "journal": "Human Reproduction Open",
          "year": 2023,
          "url": "https://www.eshre.eu/Guidelines-and-Legal/Guidelines/Recurrent-pregnancy-loss",
          "evidenceLevel": "guideline"
        },
        {
          "title": "Recurrent pregnancy loss: a committee opinion",
          "authors": "Practice Committee of the ASRM",
          "journal": "ASRM Practice Committee",
          "year": 2026,
          "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/",
          "evidenceLevel": "guideline"
        }
      ]
    },
    {
      "id": "dx-antiphospholipid-syndrome",
      "kind": "condition",
      "tags": ["her", "autoimmune", "evidence-based"],
      "title": "Antiphospholipid syndrome (APS)",
      "subtitle": "Autoimmune disorder causing blood clots and pregnancy loss",
      "body": {
        "conditionName": "Antiphospholipid syndrome (APS)",
        "category": "autoimmune",
        "description": "Antiphospholipid syndrome is an autoimmune disorder where the body produces antibodies against certain blood proteins, increasing the tendency to form blood clots. In pregnancy, these clots can form in the placental blood vessels, leading to pregnancy loss, preeclampsia, and growth restriction. APS is one of the most treatable causes of recurrent pregnancy loss.",
        "symptoms": [
          "Recurrent pregnancy loss (especially after 10 weeks)",
          "Blood clots in veins or arteries (DVT, PE)",
          "Unexplained stillbirth",
          "Severe preeclampsia or HELLP syndrome",
          "Placental insufficiency and growth restriction",
          "Some patients have no symptoms outside of pregnancy"
        ],
        "howDiagnosed": "Diagnosed by the presence of antiphospholipid antibodies on two occasions at least 12 weeks apart: lupus anticoagulant, anticardiolipin antibodies (IgG/IgM), and anti-beta-2-glycoprotein I antibodies (IgG/IgM). Must also have a clinical event (pregnancy loss, blood clot, or pregnancy complication).",
        "impactOnFertility": "APS primarily affects pregnancy maintenance rather than conception. Untreated, pregnancy loss rates are very high (up to 90% in some studies). With appropriate treatment, live birth rates improve dramatically to 70-80%.",
        "prevalence": "Found in approximately 5-15% of women with recurrent pregnancy loss. Affects about 1-5% of the general population.",
        "treatmentOptions": [
          "Low-dose aspirin (started before conception)",
          "Heparin or low-molecular-weight heparin (started at positive pregnancy test)",
          "Close monitoring with serial ultrasounds and Doppler studies",
          "Referral to maternal-fetal medicine for high-risk pregnancy management",
          "Corticosteroids in refractory cases",
          "IVF may be recommended for concurrent infertility"
        ],
        "prognosis": "With treatment (aspirin + heparin), live birth rates reach 70-80%, a dramatic improvement from untreated outcomes. Early diagnosis and treatment initiation are critical. Ongoing hematology care is recommended even outside of pregnancy.",
        "forWhom": "her"
      },
      "sources": [
        {
          "title": "ESHRE guideline: recurrent pregnancy loss: an update in 2023",
          "authors": "Bender Atik R, Christiansen OB, Elson J, et al.",
          "journal": "Human Reproduction Open",
          "year": 2023,
          "url": "https://www.eshre.eu/Guidelines-and-Legal/Guidelines/Recurrent-pregnancy-loss",
          "evidenceLevel": "guideline"
        },
        {
          "title": "2023 ACR/EULAR antiphospholipid syndrome classification criteria",
          "authors": "Barbhaiya M, Zuily S, Naden R, et al.",
          "journal": "Annals of the Rheumatic Diseases",
          "year": 2023,
          "url": "https://ard.bmj.com/content/82/10/1258",
          "evidenceLevel": "guideline"
        }
      ]
    },
    {
      "id": "dx-ashermans-syndrome",
      "kind": "condition",
      "tags": ["her", "evidence-based"],
      "title": "Asherman's syndrome",
      "subtitle": "Intrauterine adhesions (scar tissue) blocking the uterine cavity",
      "body": {
        "conditionName": "Asherman's syndrome",
        "category": "structural",
        "description": "Asherman's syndrome is a condition where scar tissue (adhesions or synechiae) forms inside the uterus, partially or completely blocking the uterine cavity. It most commonly develops after uterine surgery, particularly D&C (dilation and curettage) performed after miscarriage or delivery. The adhesions can prevent embryo implantation and normal menstruation.",
        "symptoms": [
          "Light or absent periods (hypomenorrhea or amenorrhea)",
          "Cyclic pelvic pain despite absent periods (if cavity is obstructed)",
          "Difficulty conceiving",
          "Recurrent pregnancy loss",
          "Abnormal placentation in subsequent pregnancies"
        ],
        "howDiagnosed": "Suspected when periods become light or absent after uterine surgery. Saline sonogram (SIS) may show filling defects. Definitive diagnosis and treatment is by hysteroscopy, which allows direct visualization and lysis of adhesions. HSG may show irregular filling defects.",
        "impactOnFertility": "Adhesions physically block embryo implantation and can reduce blood flow to the endometrium. Mild adhesions may cause no fertility issues, while severe cases can significantly impair implantation. After surgical treatment, many women can conceive, though the endometrium may remain thin.",
        "prevalence": "Occurs in approximately 13-20% of women after D&C for miscarriage management, and up to 40% after D&C for retained products of conception.",
        "treatmentOptions": [
          "Hysteroscopic adhesiolysis (surgical removal of scar tissue)",
          "Post-operative intrauterine balloon or stent to prevent re-adhesion",
          "Estrogen therapy to promote endometrial regrowth after surgery",
          "Repeat hysteroscopy to assess results and remove recurring adhesions",
          "IVF with gestational carrier for severe refractory cases"
        ],
        "prognosis": "Mild-to-moderate Asherman's has a good prognosis after hysteroscopic treatment, with pregnancy rates of 60-80%. Severe cases with complete cavity obliteration have lower success rates and may require multiple surgeries. Prevention through gentle surgical techniques is increasingly emphasized.",
        "forWhom": "her"
      },
      "sources": [
        {
          "title": "AAGL practice report: Practice guidelines for management of intrauterine synechiae",
          "authors": "AAGL",
          "journal": "Journal of Minimally Invasive Gynecology",
          "year": 2017,
          "url": "https://www.jmig.org/article/S1553-4650(17)30309-7/fulltext",
          "evidenceLevel": "guideline"
        }
      ]
    }
  ],
  "relationships": [],
  "localeVariants": []
}
